import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, Shield, CheckCircle, Building2, ExternalLink } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Certifications = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="certifications-section" ref={ref}>
      <div className="certifications-container">
        <motion.div 
          className="cert-content"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="cert-visual">
            <motion.div 
              className="cert-badge-large"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={isInView ? { scale: 1, opacity: 1 } : {}}
              transition={{ delay: 0.2, type: 'spring', stiffness: 100 }}
            >
              <div className="badge-ring"></div>
              <div className="badge-inner">
                <Award size={60} />
                <span className="badge-text">ISO</span>
                <span className="badge-number">9001:2008</span>
              </div>
              <div className="badge-glow"></div>
            </motion.div>
          </div>
          
          <motion.div 
            className="cert-info"
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3, type: 'spring', stiffness: 80 }}
          >
            <span className="section-tag">Quality Assured</span>
            <h2 className="cert-title">
              ISO 9001:2008
              <span className="gradient-text"> Certified</span>
            </h2>
            <p className="cert-description">
              Our ISO 9001:2008 certification demonstrates our commitment to maintaining 
              the highest standards of quality management in all our IT solutions and services.
            </p>
            
            <div className="cert-features">
              <motion.div 
                className="cert-feature"
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.4 }}
              >
                <CheckCircle size={20} />
                <span>Quality Management System</span>
              </motion.div>
              <motion.div 
                className="cert-feature"
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.5 }}
              >
                <CheckCircle size={20} />
                <span>Continuous Improvement</span>
              </motion.div>
              <motion.div 
                className="cert-feature"
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.6 }}
              >
                <CheckCircle size={20} />
                <span>Customer Satisfaction Focus</span>
              </motion.div>
              <motion.div 
                className="cert-feature"
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.7 }}
              >
                <Shield size={20} />
                <span>Industry Best Practices</span>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>

        {/* GeM Registration Card */}
        <motion.div 
          className="gem-registration-card"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.5 }}
          data-testid="gem-registration-card"
        >
          <div className="gem-card-content">
            <div className="gem-icon-wrapper">
              <Building2 size={40} />
            </div>
            <div className="gem-info-content">
              <div className="gem-header">
                <h3>Government e-Marketplace Registered</h3>
                <motion.a 
                  href="https://gem.gov.in" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="gem-link"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Visit GeM Portal
                  <ExternalLink size={14} />
                </motion.a>
              </div>
              <p className="gem-description">
                P.R. Net Com is a verified seller on GeM (Government e-Marketplace), 
                India's official online procurement portal. Government departments 
                and PSUs can directly procure our IT products and services.
              </p>
              <div className="gem-id-display">
                <span className="gem-label">GeM Seller ID</span>
                <span className="gem-id-value" data-testid="gem-id-cert">{companyInfo.gemId}</span>
              </div>
            </div>
          </div>
          <div className="gem-badge-strip">
            <span>✓ Verified Seller</span>
            <span>✓ Government Approved</span>
            <span>✓ Direct Procurement</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Certifications;
